import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { doc, getDoc, updateDoc, serverTimestamp, arrayUnion, arrayRemove, deleteDoc, writeBatch } from 'firebase/firestore';
import { db } from '../../firebase';

// Add this new component for document viewing (copied from ScholarshipApplicationsManagement.js)
const DocumentViewer = ({ url, title }) => {
  const [showPreview, setShowPreview] = useState(false);
  const [error, setError] = useState(false);
  const [showPdfViewer, setShowPdfViewer] = useState(false);
  
  // Determine file type based on URL or extension
  const getFileType = (url) => {
    if (!url) return 'unknown';
    if (url.includes('.pdf')) return 'pdf';
    if (url.match(/\.(jpeg|jpg|gif|png)$/i)) return 'image';
    if (url.includes('/image/upload/')) return 'image';
    return 'document';
  };
  
  const fileType = getFileType(url);
  
  const handleView = () => {
    if (fileType === 'pdf') {
      // For PDFs, open in iframe modal
      setShowPdfViewer(true);
    } else {
      // For other file types (images, documents), open in new tab
      window.open(url, '_blank');
    }
  };
  
  const handlePreview = () => {
    setShowPreview(true);
  };
  
  return (
    <div className="p-4 border border-gray-200 rounded-md hover:bg-gray-50 flex flex-col items-center text-center">
      {fileType === 'pdf' ? (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-[rgb(130,88,18)] mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      ) : fileType === 'image' ? (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-[rgb(130,88,18)] mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      ) : (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-[rgb(130,88,18)] mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
        </svg>
      )}
      <span className="font-medium">{title}</span>
      <div className="mt-2 flex space-x-2">
        <button 
          onClick={handleView}
          className="px-3 py-1 bg-[rgb(130,88,18)] text-white rounded-md text-sm hover:bg-[rgb(110,68,0)]"
        >
          View
        </button>
        {fileType === 'image' && (
          <button 
            onClick={handlePreview}
            className="px-3 py-1 border border-[rgb(130,88,18)] text-[rgb(130,88,18)] rounded-md text-sm"
          >
            Preview
          </button>
        )}
      </div>
      
      {/* Image Preview Modal */}
      {showPreview && fileType === 'image' && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4" onClick={() => setShowPreview(false)}>
          <div className="bg-white p-2 rounded-lg max-w-3xl max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
            <div className="relative">
              <img 
                src={url} 
                alt={title} 
                className="max-w-full h-auto"
                onError={() => setError(true)}
              />
              {error && (
                <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
                  <p className="text-red-500">Failed to load image</p>
                </div>
              )}
              <button 
                className="absolute top-2 right-2 bg-white rounded-full p-1 shadow-md"
                onClick={() => setShowPreview(false)}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* PDF Viewer Modal */}
      {showPdfViewer && fileType === 'pdf' && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4" onClick={() => setShowPdfViewer(false)}>
          <div className="bg-white p-2 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center border-b pb-2 mb-2">
              <h3 className="text-lg font-semibold">{title}</h3>
              <button 
                className="bg-white rounded-full p-1 shadow-md"
                onClick={() => setShowPdfViewer(false)}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <iframe src={url} title={title} className="flex-grow" style={{ width: '100%', height: '100%' }} />
          </div>
        </div>
      )}
    </div>
  );
};

const ScholarshipReviews = ({ applicationId, embedded = false, onDeleted }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [applicationDetails, setApplicationDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [commentText, setCommentText] = useState('');
  const [addingComment, setAddingComment] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [commentToDelete, setCommentToDelete] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [deleteAppModalOpen, setDeleteAppModalOpen] = useState(false);
  const [deletingApp, setDeletingApp] = useState(false);
  const [deleted, setDeleted] = useState(false);

  // Refactored fetch function for reuse
  const fetchApplicationDetails = async () => {
    setRefreshing(true);
    if (!applicationId) {
      setLoading(false);
      setRefreshing(false);
      return;
    }
    try {
      const appRef = doc(db, 'scholarship_applications', applicationId);
      const appSnapshot = await getDoc(appRef);
      if (appSnapshot.exists()) {
        setApplicationDetails({ id: appSnapshot.id, ...appSnapshot.data() });
      } else {
        setApplicationDetails(null);
        setDeleted(true);
        if (onDeleted) onDeleted();
      }
    } catch (error) {
      console.error('Error fetching application details:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchApplicationDetails();
    // eslint-disable-next-line
  }, [applicationId]);

  // Add status update function
  const updateApplicationStatus = async (newStatus) => {
    if (!applicationId || !newStatus) return;
    
    setUpdatingStatus(true);
    try {
      const applicationRef = doc(db, "scholarship_applications", applicationId);
      
      await updateDoc(applicationRef, {
        status: newStatus,
        updatedAt: serverTimestamp()
      });
      
      // Update local state
      setApplicationDetails(prev => ({
        ...prev,
        status: newStatus,
        updatedAt: new Date() // Use client-side timestamp for immediate update
      }));
    } catch (error) {
      console.error("Error updating application status:", error);
    } finally {
      setUpdatingStatus(false);
    }
  };

  const addAdminComment = async () => {
    if (!commentText.trim()) return; // Prevent adding empty comments
    setAddingComment(true);
    try {
      const comment = {
        text: commentText,
        adminId: user?.uid || 'unknown',
        timestamp: serverTimestamp(),
      };

      const applicationRef = doc(db, "scholarship_applications", applicationId);
      
      await updateDoc(applicationRef, {
        adminComments: arrayUnion(comment)
      });
      
      // Update local state to reflect the new comment
      setApplicationDetails(prev => ({
        ...prev,
        adminComments: [...(prev.adminComments || []), { ...comment, timestamp: new Date() }]
      }));
      
      setCommentText(''); // Clear textarea after adding comment
    } catch (error) {
      console.error("Error adding comment:", error);
      // Handle error (e.g., show a message)
    } finally {
      setAddingComment(false);
    }
  };

  // Format date and time
  const formatDateTime = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  // Add delete comment handler
  const handleDeleteClick = (comment) => {
    setCommentToDelete(comment);
    setDeleteModalOpen(true);
  };

  const confirmDeleteComment = async () => {
    if (!commentToDelete) return;
    try {
      const applicationRef = doc(db, "scholarship_applications", applicationId);
      await updateDoc(applicationRef, {
        adminComments: arrayRemove(commentToDelete)
      });
      setApplicationDetails(prev => ({
        ...prev,
        adminComments: (prev.adminComments || []).filter(c => {
          return !(
            c.text === commentToDelete.text &&
            c.adminId === commentToDelete.adminId &&
            (c.timestamp?.toString?.() === commentToDelete.timestamp?.toString?.() || c.timestamp === commentToDelete.timestamp)
          );
        })
      }));
      setDeleteModalOpen(false);
      setCommentToDelete(null);
    } catch (error) {
      console.error('Error deleting comment:', error);
      alert('Failed to delete comment.');
      setDeleteModalOpen(false);
      setCommentToDelete(null);
    }
  };

  const cancelDeleteComment = () => {
    setDeleteModalOpen(false);
    setCommentToDelete(null);
  };

  // Add delete application handler
  const handleDeleteApplication = () => {
    setDeleteAppModalOpen(true);
  };

  const confirmDeleteApplication = async () => {
    setDeletingApp(true);
    try {
      // Ensure we have the application details
      let app = applicationDetails;
      if (!app) {
        const appRef = doc(db, 'scholarship_applications', applicationId);
        const appSnap = await getDoc(appRef);
        if (appSnap.exists()) {
          app = { id: appSnap.id, ...appSnap.data() };
        } else {
          setDeleted(true);
          setDeleteAppModalOpen(false);
          if (onDeleted) onDeleted();
          return;
        }
      }
      const { userId, scholarshipId, id } = app;
      if (!userId || !scholarshipId || !id) {
        alert('Missing user or scholarship information.');
        setDeleteAppModalOpen(false);
        setDeletingApp(false);
        return;
      }
      // Use a batch for atomicity
      const batch = writeBatch(db);
      batch.delete(doc(db, 'scholarship_applications', id));
      batch.delete(doc(db, 'users', userId, 'applications', id));
      batch.delete(doc(db, 'scholarships', scholarshipId, 'applications', id));
      await batch.commit();
      setDeleted(true);
      setDeleteAppModalOpen(false);
      if (onDeleted) onDeleted();
    } catch (error) {
      alert('Failed to delete application everywhere.');
      setDeleteAppModalOpen(false);
    } finally {
      setDeletingApp(false);
    }
  };

  const cancelDeleteApplication = () => {
    setDeleteAppModalOpen(false);
  };

  if (deleted) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-gray-500">
        <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
        <p>Application deleted.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[rgb(130,88,18)]"></div>
      </div>
    );
  }

  if (!applicationDetails) {
    return (
      <div className="text-center text-red-600">Error loading application details.</div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Review Application: {applicationDetails.fullName || 'Unknown Applicant'}</h1>
        <div className="flex gap-2">
          <button
            onClick={fetchApplicationDetails}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
            disabled={refreshing}
          >
            {refreshing ? 'Refreshing...' : 'Save & Refresh'}
          </button>
          <button
            onClick={handleDeleteApplication}
            className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
          >
            Delete Application
          </button>
        </div>
      </div>
      {/* Back Button - only show if not embedded */}
      {!embedded && (
        <div className="mb-4">
          <button 
            onClick={() => navigate('/admin/scholarship-applications')}
            className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 transition-colors duration-300"
          >
            &larr; Back to Applications
          </button>
        </div>
      )}
      {/* Display Application Details */}
      <div className="bg-white p-6 rounded-lg shadow-md mb-6">
        <div className="flex justify-between items-start mb-6">
          <h2 className="text-xl font-semibold">Application Details</h2>
          <div className="flex flex-col items-end gap-2">
            <select
              value={applicationDetails.status || 'pending'}
              onChange={(e) => updateApplicationStatus(e.target.value)}
              disabled={updatingStatus}
              className="px-3 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[rgb(130,88,18)] disabled:opacity-50"
            >
              <option value="pending">Pending</option>
              <option value="under_review">Under Review</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="waitlisted">Waitlisted</option>
            </select>
            <span className={`px-2 py-1 text-xs rounded-full ${
              applicationDetails.status === 'pending' ? 'bg-blue-100 text-blue-800' :
              applicationDetails.status === 'under_review' ? 'bg-yellow-100 text-yellow-800' :
              applicationDetails.status === 'approved' ? 'bg-green-100 text-green-800' :
              applicationDetails.status === 'rejected' ? 'bg-red-100 text-red-800' :
              applicationDetails.status === 'waitlisted' ? 'bg-purple-100 text-purple-800' :
              'bg-gray-100 text-gray-800'
            }`}>
              {applicationDetails.status?.replace('_', ' ').charAt(0).toUpperCase() + 
                applicationDetails.status?.replace('_', ' ').slice(1) || 'Unknown'}
            </span>
          </div>
        </div>
        
        {/* Personal Information */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold mb-3">Personal Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="mb-1"><span className="font-medium">Full Name:</span> {applicationDetails.fullName || 'Not provided'}</p>
              <p className="mb-1"><span className="font-medium">Email:</span> {applicationDetails.email || 'Not provided'}</p>
              <p className="mb-1"><span className="font-medium">Phone:</span> {applicationDetails.phone || 'Not provided'}</p>
            </div>
            <div>
              <p className="mb-1"><span className="font-medium">Date of Birth:</span> {applicationDetails.dateOfBirth || 'Not provided'}</p>
              <p className="mb-1"><span className="font-medium">Address:</span> {applicationDetails.address || 'Not provided'}</p>
              <p className="mb-1">
                <span className="font-medium">Applied:</span> {formatDateTime(applicationDetails.appliedAt)}
              </p>
            </div>
          </div>
        </div>

        {/* Collected Educational Details (from form) */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold mb-3">Collected Educational Details</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="mb-1"><span className="font-medium">Institution:</span> {applicationDetails.institution || 'Not provided'}</p>
              <p className="mb-1"><span className="font-medium">Level:</span> {applicationDetails.level || 'Not provided'}</p>
            </div>
            <div>
              <p className="mb-1"><span className="font-medium">Department:</span> {applicationDetails.department || 'Not provided'}</p>
              <p className="mb-1"><span className="font-medium">CGPA:</span> {applicationDetails.cgpa || 'Not provided'}</p>
            </div>
          </div>
        </div>

        {/* Educational Background (from user profile, if applicable) */}
        {applicationDetails.education && (
          <div className="mb-6">
            <h3 className="text-lg font-semibold mb-3">Educational Background (from user profile)</h3>
            <div className="space-y-4">
              {Array.isArray(applicationDetails.education) ? (
                applicationDetails.education.map((edu, index) => (
                  <div key={index} className="p-4 border border-gray-200 rounded-md">
                    <p className="mb-1"><span className="font-medium">Institution:</span> {edu.institution || 'Not provided'}</p>
                    <p className="mb-1"><span className="font-medium">Degree/Level:</span> {edu.degree || 'Not provided'}</p>
                    <p className="mb-1"><span className="font-medium">Field of Study:</span> {edu.fieldOfStudy || 'Not provided'}</p>
                    <p className="mb-1"><span className="font-medium">GPA:</span> {edu.gpa || 'Not provided'}</p>
                    <p><span className="font-medium">Graduation Year:</span> {edu.graduationYear || 'Not provided'}</p>
                  </div>
                ))
              ) : ( // Handle single education entry
                <div className="p-4 border border-gray-200 rounded-md">
                  <p className="mb-1"><span className="font-medium">Institution:</span> {applicationDetails.education.institution || 'Not provided'}</p>
                  <p className="mb-1"><span className="font-medium">Degree/Level:</span> {applicationDetails.education.degree || 'Not provided'}</p>
                  <p className="mb-1"><span className="font-medium">Field of Study:</span> {applicationDetails.education.fieldOfStudy || 'Not provided'}</p>
                  <p className="mb-1"><span className="font-medium">GPA:</span> {applicationDetails.education.gpa || 'Not provided'}</p>
                  <p><span className="font-medium">Graduation Year:</span> {applicationDetails.education.graduationYear || 'Not provided'}</p>
                </div>
              )}
            </div>
          </div>
        )}
        
        {/* Financial Information */}
        {applicationDetails.financialInfo && (
          <div className="mb-6">
            <h3 className="text-lg font-semibold mb-3">Financial Information</h3>
            <div className="p-4 border border-gray-200 rounded-md">
              <p className="mb-1">
                <span className="font-medium">Annual Household Income:</span> ${applicationDetails.financialInfo.annualIncome || 'Not provided'}
              </p>
              <p className="mb-1">
                <span className="font-medium">Number of Dependents:</span> {applicationDetails.financialInfo.dependents || 'Not provided'}
              </p>
              <p>
                <span className="font-medium">Other Financial Aid:</span> {applicationDetails.financialInfo.otherAid ? 'Yes' : 'No'}
              </p>
              {applicationDetails.financialInfo.otherAidDetails && (
                <p className="mt-2">
                  <span className="font-medium">Other Aid Details:</span> {applicationDetails.financialInfo.otherAidDetails}
                </p>
              )}
            </div>
          </div>
        )}
        
        {/* Attachments */}
        {(applicationDetails.documents && Object.values(applicationDetails.documents).some(url => url)) && (
          <div className="mb-6">
            <h3 className="text-lg font-semibold mb-3">Documents</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {Object.entries(applicationDetails.documents).map(([key, value]) => {
                if (!value || typeof value !== 'string' || !value.startsWith('http')) return null;
                const title = key;
                return (
                  <DocumentViewer 
                    key={key}
                    url={value} 
                    title={title}
                  />
                );
              })}
            </div>
          </div>
        )}

        {/* Essay Text */}
        {applicationDetails.essay && (
          <div className="mb-6">
            <h3 className="text-lg font-semibold mb-3">Essay</h3>
            <div className="p-4 bg-gray-50 rounded-md">
              <p className="whitespace-pre-line">{applicationDetails.essay}</p>
            </div>
          </div>
        )}

        {/* Additional Information */}
        {applicationDetails.additionalInfo && (
          <div className="mb-6">
            <h3 className="text-lg font-semibold mb-3">Additional Information</h3>
            <div className="p-4 bg-gray-50 rounded-md">
              <p className="whitespace-pre-line">{applicationDetails.additionalInfo}</p>
            </div>
          </div>
        )}
      </div>

      {/* Admin Comments Section */}
      <div className="bg-white p-6 rounded-lg shadow-md">
        <h3 className="text-xl font-semibold mb-4">Admin Comments</h3>
        <div className="space-y-4 mb-4">
          {applicationDetails.adminComments && applicationDetails.adminComments.length > 0 ? (
            applicationDetails.adminComments.map((comment, index) => (
              <div key={index} className="p-3 bg-gray-100 rounded-md flex justify-between items-start">
                <div>
                  <p className="text-sm text-gray-700 whitespace-pre-line">{comment.text}</p>
                  {/* TODO: Display admin name if available */}
                  <p className="text-xs text-gray-500 mt-1">{comment.timestamp ? formatDateTime(comment.timestamp) : 'Just added'}</p>
                </div>
                <button
                  className="ml-4 text-red-600 hover:underline text-xs"
                  onClick={() => handleDeleteClick(comment)}
                  title="Delete comment"
                >
                  Delete
                </button>
              </div>
            ))
          ) : (
            <p className="text-sm text-gray-500">No comments yet.</p>
          )}
        </div>
        
        {/* Add New Comment Form */}
        <div className="mt-4">
          <textarea
            className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[rgb(130,88,18)]"
            rows="3"
            placeholder="Add a comment or next instruction..."
            value={commentText}
            onChange={(e) => setCommentText(e.target.value)}
            disabled={addingComment}
          ></textarea>
          <button
            onClick={addAdminComment}
            className="mt-2 px-4 py-2 bg-[rgb(130,88,18)] text-white rounded-md hover:bg-[rgb(110,75,15)] disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={!commentText.trim() || addingComment}
          >
            {addingComment ? 'Adding Comment...' : 'Add Comment'}
          </button>
        </div>
      </div>
      {/* Modal for delete confirmation */}
      {deleteModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded-lg shadow-lg p-6 max-w-sm w-full">
            <h2 className="text-lg font-semibold mb-4">Delete Comment</h2>
            <p className="mb-6">Are you sure you want to delete this comment?</p>
            <div className="flex justify-end gap-2">
              <button
                className="px-4 py-2 rounded bg-gray-200 text-gray-700 hover:bg-gray-300"
                onClick={cancelDeleteComment}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 rounded bg-red-600 text-white hover:bg-red-700"
                onClick={confirmDeleteComment}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Modal for delete application confirmation */}
      {deleteAppModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded-lg shadow-lg p-6 max-w-sm w-full">
            <h2 className="text-lg font-semibold mb-4">Delete Application</h2>
            <p className="mb-6">Are you sure you want to delete this application? This action cannot be undone.</p>
            <div className="flex justify-end gap-2">
              <button
                className="px-4 py-2 rounded bg-gray-200 text-gray-700 hover:bg-gray-300"
                onClick={cancelDeleteApplication}
                disabled={deletingApp}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 rounded bg-red-600 text-white hover:bg-red-700"
                onClick={confirmDeleteApplication}
                disabled={deletingApp}
              >
                {deletingApp ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ScholarshipReviews; 